/**
 * Embedded sender editor: is the field editor running inside a partner iframe (embed session, scope
 * "creation")? Shared editor components branch on this to re-point their calls to the embed endpoints and
 * to hide what the embed session cannot do (AI, templates, in-person signing). Inert everywhere else.
 */
import strings from "global/constants/StringConstants";
import { IS_EMBED } from "./embedMode";
import { getEmbedContext } from "./embedBridge";

export const isEmbedEditorMode = (): boolean =>
  IS_EMBED && getEmbedContext().scope === "creation";

export const embedEditorRequestId = (): string => getEmbedContext().requestId;

/**
 * A signer's delivery mode in the embedded wizard -- the same rule the server applies on save. The draft's
 * own signers arrive with their mode set (EmbedEditorScreen fills a blank one in as EMAIL, as the server
 * reads it), so a signer with no mode is one the wizard added: it signs in the partner's app (EMBEDDED).
 */
export const embedDeliveryModeOf = (mode?: string): string =>
  mode === strings.deliveryModes.email ||
  mode === strings.deliveryModes.inPerson ||
  mode === strings.deliveryModes.embedded
    ? mode
    : strings.deliveryModes.embedded;

/** The branding the signers' emails will carry, from the editor bundle; drives the Step 4 preview. */
let emailBranding: any = null;

export const setEmbedEmailBranding = (branding: any) => {
  emailBranding = branding ?? null;
};

export const getEmbedEmailBranding = (): any => emailBranding;

/** The identity-verification methods a vendor runs here, from the editor bundle (the app reads them logged in). */
let idvMethods: string[] = [];

export const setEmbedIdvMethods = (methods?: string[] | null) => {
  idvMethods = Array.isArray(methods) ? methods : [];
};

export const getEmbedIdvMethods = (): string[] => idvMethods;

/**
 * Signers renumbered to their position (0, 1, 2...) with every field pointed at the renumbered signer.
 * The server renumbers the same way when it saves the draft, and the send that follows must carry the
 * SAME ids: after a signer is removed and another added the wizard reuses the freed id, so the ids are no
 * longer positional, and fields sent with the old ids would land on the wrong signer or be refused.
 */
export const withPositionalSignerIds = (signers: any[], files: any[]) => {
  const positions = new Map<number, number>();
  const nextSigners = (signers || []).map((signer: any, position: number) => {
    positions.set(signer.id, position);
    return { ...signer, id: position };
  });
  const nextFiles = (files || []).map((file: any) => ({
    ...file,
    fields: (file.fields || []).map((field: any) =>
      positions.has(field.signerIndex)
        ? { ...field, signerIndex: positions.get(field.signerIndex) }
        : field,
    ),
  }));
  return { signers: nextSigners, files: nextFiles };
};
